import * as React from "react";
import { I18N_NS } from "Config/i18n";
import { useTranslation } from "react-i18next";
import { Row, Col, Checkbox, Switch, Divider, Tooltip, Form } from "antd";
import Title from "antd/lib/typography/Title";
import { viewsStore } from "../Stores/ViewsStore";
import { Button } from "Components/General/Button";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { Loader } from "Components/General/Loader";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { useForm } from "antd/lib/form/Form";

interface ViewsExportModalProps extends ModalComponentProps {
  fileIds: number[];
  viewExportInfo: API_ViewRequest;
}

export const ViewsExportModal = ({ hideModal, fileIds, viewExportInfo }: ViewsExportModalProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [form] = useForm();
  const [detailsList, setDetailsList] = React.useState<string[]>([]);
  const [metadataList, setMetadataList] = React.useState<string[]>([]);
  const [disabledDistributionWithDates, setDisabledDistributionWithDates] = React.useState(true);
  const [loadingExcel, setLoadingExcel] = React.useState<boolean>(false);

  const handleExcelCreation = async (values) => {
    setLoadingExcel(true);
    const formData: API_ViewExcelMultipleFilesRequest = {
      viewExcelExportOptions: {
        ...values,
        viewRequest: viewExportInfo,
      },
      fileIds,
    };
    try {
      await viewsStore.createExcel(formData);
      hideModal();
    } catch (error) {
      notificationError(t("Error"), t("Excel file was not created"));
    } finally {
      setLoadingExcel(false);
    }
  };

  React.useEffect(() => {
    (async () => {
      const response = await viewsStore.fetchExportOptions();
      if (response) {
        setDetailsList(response.fileDetails);
        setMetadataList(response.metadata);
        form.setFieldsValue({
          fileDetails: response.fileDetails.slice(),
          metadata: [],
          withLogo: response.withLogo,
          sortByName: response.sortByName,
          allFileVersions: response.allFileVersions,
          withMetadataDescription: response.withMetadataDescription,
          distributionViaMail: response.distributionViaMail,
          distributionViaPrint: response.distributionViaPrint,
          distributionWithDates: response.distributionWithDates,
        });
      }
    })();
  }, []);

  const handleValuesChange = (_, allValues) => {
    if (allValues.distributionViaMail || allValues.distributionViaPrint) {
      setDisabledDistributionWithDates(false);
    } else if (!allValues.distributionViaMail && !allValues.distributionViaPrint) {
      setDisabledDistributionWithDates(true);
      form.setFieldValue("distributionWithDates", false);
    }
  };

  if (viewsStore.state === "LOADING") {
    return <Loader />;
  }

  return (
    <Form onFinish={handleExcelCreation} form={form} onValuesChange={handleValuesChange}>
      <Row gutter={24}>
        <Col flex="1">
          <Form.Item name="fileDetails" noStyle>
            <CheckboxGroup options={detailsList} title={t("List shall contain these columns")} />
          </Form.Item>
          <Form.Item name="metadata" noStyle>
            <CheckboxGroup options={metadataList} title={t("Metadata")} />
          </Form.Item>
        </Col>
        <Col flex="1">
          <Title level={4} className="mb-md">
            {t("Options")}
          </Title>
          <Form.Item name="withLogo" noStyle>
            <SwitchItem label={t("Integrate project logo")} />
          </Form.Item>
          <Form.Item name="withMetadataDescription" noStyle>
            <SwitchItem label={t("Metadata values with description")} />
          </Form.Item>
          <Form.Item name="sortByName" noStyle>
            <SwitchItem label={t("Sort by name")} />
          </Form.Item>
          <Form.Item name="allFileVersions" noStyle>
            <SwitchItem label={t("All versions/indexes of a file")} />
          </Form.Item>
          <Form.Item name="distributionViaMail" noStyle>
            <SwitchItem
              label={t("Distribution via infomail")}
              tooltip={t("Distribution info will only be added for files to which you have owner rights.")}
            />
          </Form.Item>
          <Form.Item name="distributionViaPrint" noStyle>
            <SwitchItem
              label={t("Distribution via print job")}
              tooltip={t("Distribution info will only be added for files to which you have owner rights.")}
            />
          </Form.Item>
          <Form.Item name="distributionWithDates" noStyle>
            <SwitchItem label={t("With date of first distribution")} disabled={disabledDistributionWithDates} />
          </Form.Item>
        </Col>
      </Row>
      <ButtonsRow justify="end" style={{ marginTop: 30 }}>
        <Button type="primary" htmlType="submit" loading={loadingExcel}>
          {t("Create Excel")}
        </Button>
      </ButtonsRow>
    </Form>
  );
};

interface CheckboxGroupProps {
  options: string[];
  title: string;
  onChange?: (opts: string[]) => void;
  value?: string[];
}

function CheckboxGroup({ options, title, value = [], onChange }: CheckboxGroupProps) {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [isIndeterminate, setIsIndeterminate] = React.useState(false);
  const [isCheckAll, setIsCheckAll] = React.useState(false);

  const handleCheckAll = (e: CheckboxChangeEvent) => {
    setIsCheckAll(e.target.checked);
    if (e.target.checked) {
      onChange(options.slice());
    } else {
      onChange([]);
    }
  };

  const handleChange = (checkedValues) => {
    onChange(checkedValues);
  };

  React.useEffect(() => {
    if (!value.length) {
      setIsCheckAll(false);
      setIsIndeterminate(false);
    } else if (value.length === options.length) {
      setIsCheckAll(true);
      setIsIndeterminate(false);
    } else if (value.length > 0 && value.length < options.length) {
      setIsCheckAll(false);
      setIsIndeterminate(true);
    }
  }, [value, options]);

  return (
    <>
      <Title level={4} className="mb-md">
        {title}
      </Title>
      <Checkbox indeterminate={isIndeterminate} onChange={handleCheckAll} checked={isCheckAll}>
        {t("Check all")}
      </Checkbox>
      <Divider style={{ margin: "8px 0" }} />
      <Checkbox.Group className="custom-checkbox-group mb-lg" value={value} options={options} onChange={handleChange} />
    </>
  );
}

interface SwitcherItemProps {
  value?: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
  label: string;
  tooltip?: string;
}

function SwitchItem({ label, disabled, onChange, tooltip, value }: SwitcherItemProps) {
  return (
    <div className="mb-2">
      <Tooltip placement="topLeft" title={tooltip}>
        <Switch onChange={onChange} disabled={disabled} checked={value} />
      </Tooltip>
      <span style={{ marginLeft: 8 }}>{label}</span>
    </div>
  );
}
